import React from 'react';
import { useSnackbar } from 'notistack';
import { MainDialog, MainDialogProps } from '@components/dialog/main.dialog';
import { MainDialogTitle } from '@components/dialog/main.dialog.title';
import { MainDialogContent } from '@components/dialog/main.dialog.content';
import { Loading } from '@components/loading';
import { ErrorDisplay } from '@components/error/error.display';
import { Button, DialogActions } from '@material-ui/core';
import { FormData } from '@domain/hooks/use.form.data';
import { CenteredRoot, DescText } from '@styles/common';
import { noop } from '@domain/utils/function.utils';

interface RequestDialogProps<T, R> extends MainDialogProps {
  formData: FormData<T, R>;
  icon?: string;
  title?: string;
  successMsg?: string;
  onSuccess?(): void;
  children?: React.ReactNode;
}

export function RequestDialog<T, R>({ formData, icon, title, successMsg, onSuccess, children, ...rest }: RequestDialogProps<T, R>) {
  const { enqueueSnackbar } = useSnackbar();
  const { open } = rest;

  const { loading, error, submitAsync } = formData;

  const childrenIsString = typeof children === 'string';

  function success() {
    open.disable();
    if (successMsg) enqueueSnackbar(successMsg, { variant: 'success' });
    if (onSuccess) onSuccess();
  }

  function submit(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    submitAsync(e).then(success, noop);
  }

  return (
    <MainDialog disableBackdropClick {...rest}>
      {title && (
        <MainDialogTitle hideCloseButton icon={icon}>
          {title}
        </MainDialogTitle>
      )}

      <MainDialogContent>
        <CenteredRoot>
          <Loading loading={loading} />

          {childrenIsString && <DescText>{children}</DescText>}
          {!childrenIsString && children}

          <ErrorDisplay error={error} />
        </CenteredRoot>
      </MainDialogContent>

      <DialogActions>
        <Button onClick={open.disable} color='primary' disabled={loading}>
          Cancel
        </Button>
        <Button onClick={submit} color='primary' disabled={loading}>
          OK
        </Button>
      </DialogActions>
    </MainDialog>
  );
}
